/**
 * Does a block's value fit its box? (KPR-1861, KPE-041)
 *
 * On a form the box is the limit: `.page-block` is clipped at its own edges, so
 * whatever a value needs beyond them is not drawn and not printed. This says what
 * that is, so nothing is lost silently on a Department form.
 *
 * THE BROWSER MEASURES, NOT THIS FILE. The lines of a value are read back from the
 * layout the sheet was just drawn with, so the font, the size, the wrapping and the
 * unit conversion are the ones the printed sheet uses, and a warning cannot disagree
 * with the paper by a metric of its own. What lives here is the rule that turns
 * rectangles into an answer, and it is pure: it takes a clip and the pieces of a
 * block's content, and knows nothing of blocks, forms or elements. Auto scale
 * (KPR-1862, below) asks it again with a smaller font until it says nothing is cut,
 * and a repeating group (KPR-1863) asks it of each cell of a ruled line.
 *
 * A line is cut when the part of it that has ink lies past the clip. The browser's
 * rectangle for a word is the font's content area (its ascent and descent), which is
 * a little taller than the ink and a good deal shorter than the line box, so a
 * tenth of a word's height at the top and the bottom is empty space and is let
 * through. Half a line is cut. A line that only touches the edge is not: a layout is
 * in fractions of a pixel.
 */

/** Layout works in fractions of a pixel; this much past an edge is rounding, not a cut. */
export const FIT_TOLERANCE = 0.5;

/** The share of a word's height, top and bottom, that holds no ink: the room a font keeps for accents and tails. */
export const EMPTY_SHARE = 0.1;

/** A value this long is read up to here: a block holds a field, not a book. A longer one is counted from what was read. */
export const MAX_WORDS = 5000;

const wide = piece => piece.right - piece.left;
const high = piece => piece.bottom - piece.top;

const pastEdge = (clip, piece) => {
	// A word's top and bottom have empty space in them; a picture's do not.
	const room = piece.kind === 'graphic' ? FIT_TOLERANCE : Math.max(FIT_TOLERANCE, high(piece) * EMPTY_SHARE);
	return {
		vertical: piece.bottom - clip.bottom > room || clip.top - piece.top > room,
		horizontal: piece.right - clip.right > FIT_TOLERANCE || clip.left - piece.left > FIT_TOLERANCE,
	};
};

/**
 * Groups text pieces into the lines they sit on: pieces that share more than half
 * of the smaller one's height are one line.
 */
function linesOf(pieces) {
	const sorted = [...pieces].sort((a, b) => a.top - b.top || a.left - b.left);
	const lines = [];
	for (const piece of sorted) {
		const line = lines[lines.length - 1];
		const shared = line ? Math.min(line.bottom, piece.bottom) - Math.max(line.top, piece.top) : -1;
		if (line && shared > Math.min(high(line), high(piece)) / 2) {
			line.top = Math.min(line.top, piece.top);
			line.bottom = Math.max(line.bottom, piece.bottom);
			line.pieces.push(piece);
		} else {
			lines.push({ top: piece.top, bottom: piece.bottom, pieces: [piece] });
		}
	}
	return lines;
}

/**
 * What of this content lies outside the clip, or null when all of it is inside.
 *
 * `clip` is `{ left, top, right, bottom }`. `pieces` are `{ kind, left, top, right,
 * bottom }` with `kind` 'text' (a word of the value) or 'graphic' (a picture). The
 * answer counts lines and pictures: `{ lines, cutLines, cutDown, pictures, cutPictures }`,
 * where `cutDown` is the lines cut at the bottom or top and the others are cut only
 * at a side, as a word wider than the box is.
 */
export function cutOf(clip, pieces) {
	const seen = (pieces || []).filter(piece => wide(piece) > 0 && high(piece) > 0);
	const lines = linesOf(seen.filter(piece => piece.kind !== 'graphic'));
	const pictures = seen.filter(piece => piece.kind === 'graphic');
	let cutLines = 0;
	let cutDown = 0;
	for (const line of lines) {
		const hit = line.pieces.map(piece => pastEdge(clip, piece));
		if (hit.some(edge => edge.vertical)) { cutLines += 1; cutDown += 1; } else if (hit.some(edge => edge.horizontal)) cutLines += 1;
	}
	const cutPictures = pictures.filter(piece => {
		const edge = pastEdge(clip, piece);
		return edge.vertical || edge.horizontal;
	}).length;
	if (!cutLines && !cutPictures) return null;
	return { lines: lines.length, cutLines, cutDown, pictures: pictures.length, cutPictures };
}

/** The answer in words: "2 of 3 lines cut". Never a color, never a number alone. */
export function describeCut(cut) {
	const parts = [];
	if (cut.cutLines) {
		if (cut.lines === 1) parts.push(cut.cutDown ? 'the line is cut' : 'the end of the line is cut');
		else if (cut.cutDown) parts.push(`${cut.cutLines} of ${cut.lines} lines cut`);
		else parts.push(`the end of ${cut.cutLines} of ${cut.lines} lines is cut`);
	}
	if (cut.cutPictures) parts.push(cut.cutPictures === 1 ? 'part of an image is cut' : `parts of ${cut.cutPictures} images are cut`);
	return parts.join(', and ');
}

// What draws as one picture. Their own box is the piece; nothing inside is read.
const GRAPHIC = new Set(['IMG', 'SVG', 'CANVAS', 'VIDEO', 'PICTURE', 'IFRAME', 'OBJECT', 'INPUT', 'TEXTAREA', 'SELECT']);

const rectOf = (rect, kind, font = 0) => ({ kind, left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom, font });

/**
 * The words and pictures a block draws, with the rectangles the browser gave them.
 * A word at a time, not a text node: the spaces at a line's end hang past the box
 * without being ink, and a text node's rectangles include them. A word also carries
 * its font size in CSS px (`font`), which is how auto scale knows how far a block can
 * go before its smallest text reaches the floor.
 */
export function readPieces(node, limit = MAX_WORDS) {
	const pieces = [];
	const doc = node.ownerDocument;
	const view = doc.defaultView;
	const range = doc.createRange();
	let words = 0;
	const read = (parent, font) => {
		for (const child of parent.childNodes) {
			if (words >= limit) return;
			if (child.nodeType === 3) {
				for (const match of child.nodeValue.matchAll(/\S+/g)) {
					range.setStart(child, match.index);
					range.setEnd(child, match.index + match[0].length);
					for (const rect of range.getClientRects()) pieces.push(rectOf(rect, 'text', font));
					words += 1;
					if (words >= limit) return;
				}
			} else if (child.nodeType === 1) {
				const style = view.getComputedStyle(child);
				// Not drawn, so not cut: a hidden affordance, a collapsed line, text for a
				// screen reader, and the Show and Copy buttons of a masked value, which are
				// on the screen and never on the paper (print.css).
				if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue;
				if (/^rect\((0(px)?[ ,]*){4}\)$/.test(style.clip || '') || child.classList.contains('masked-value__actions')) continue;
				if (GRAPHIC.has(child.tagName.toUpperCase())) pieces.push(rectOf(child.getBoundingClientRect(), 'graphic'));
				else read(child, parseFloat(style.fontSize) || font);
			}
		}
	};
	read(node, parseFloat(view.getComputedStyle(node).fontSize) || 0);
	return pieces;
}

/** The block's box, cut by its sheet: a block may run past the bottom, and the sheet clips it. */
export function clipOf(block) {
	const box = block.getBoundingClientRect();
	const sheet = block.closest('.page-sheet')?.getBoundingClientRect();
	if (!sheet) return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
	const left = Math.max(box.left, sheet.left);
	const top = Math.max(box.top, sheet.top);
	return { left, top, right: Math.max(left, Math.min(box.right, sheet.right)), bottom: Math.max(top, Math.min(box.bottom, sheet.bottom)) };
}

/**
 * AUTO SCALE TO FIT (KPR-1862, KPE-041). A block with `style.autoScale` on a form
 * shrinks its text before it is cut, to a floor, and no further: the largest size at
 * which `cutOf` says nothing is cut, never above the size the person set.
 *
 * The size is a number between 0 and 1 written on the block as `--fit-scale`, and the
 * block's font sizes are `calc(<set size> * var(--fit-scale, 1))` (print.css and
 * PageLayoutRender), so it is the browser that lays the shrunk text out, measures it
 * and prints it, and the paper gets the very DOM the screen showed: there is no
 * second computation at print time to disagree with the first.
 */

/** The smallest text auto scale will draw, in points (the owner's ruling, 2026-11-01). */
export const FLOOR_PT = 6;

/** How finely the size is found, in points: finer than anyone sets a size, coarse enough for about seven probes. */
export const STEP_PT = 0.1;

/** The custom property that carries a block's scale. */
export const SCALE_VAR = '--fit-scale';

const MAX_PROBES = 24;

/**
 * The largest scale in [lowest, 1] at which `fitsAt(scale)` is true, found by
 * halving, to `resolution`, then one `resolution` smaller still. Pure: `fitsAt` is the
 * caller's question (the browser's measure), and it is only ever asked at a scale
 * between `lowest` and 1.
 *
 * The last step down is a margin. The largest size that fits is one whose last word
 * touches the box's edge, and the paper is laid out again by the printer, whose text
 * can differ from the screen's by a hair; one tenth of a point is room for that, and
 * is the price of a value that does not lose a line in the print dialog.
 *
 * `{ scale, fits }`. Full size when it fits; `lowest` with `fits: false` when even
 * that cuts (the value is then cut, and told); 1 with `fits: false` when `lowest` is
 * not under 1 (a block already at the floor has nothing to give). The answer is
 * always a scale that was asked about, so it is one that was seen to fit. Wrapping is
 * taken to be monotonic (a smaller size never needs more room), as it is to a tenth
 * of a point: a value that fits at no size the floor allows is cut at the floor.
 */
export function scaleToFit(fitsAt, lowest, resolution, { overAtFull = false } = {}) {
	if (!overAtFull && fitsAt(1)) return { scale: 1, fits: true };
	if (!(lowest < 1)) return { scale: 1, fits: false };
	if (!fitsAt(lowest)) return { scale: lowest, fits: false };
	const step = Math.max(resolution, 1e-4);
	let fit = lowest;
	let over = 1;
	for (let probes = 0; over - fit > step && probes < MAX_PROBES; probes += 1) {
		const mid = (fit + over) / 2;
		if (fitsAt(mid)) fit = mid; else over = mid;
	}
	const margin = Math.max(lowest, fit - step);
	return { scale: margin < fit && fitsAt(margin) ? margin : fit, fits: true };
}

/** The lowest scale a block can go to: its smallest text at the floor. 1 when it is at or under the floor already. */
export function lowestScale(smallestPx) {
	const pt = smallestPx * 0.75;
	return pt > FLOOR_PT ? FLOOR_PT / pt : 1;
}

/** The smallest font size, in px, among a block's words; 0 when it has none. */
export const smallestFont = pieces => pieces.reduce((least, piece) => (piece.kind === 'text' && piece.font > 0 && (!least || piece.font < least) ? piece.font : least), 0);

const writeScale = (block, scale) => {
	const text = String(Math.round(scale * 10000) / 10000);
	if (block.style.getPropertyValue(SCALE_VAR) !== text) block.style.setProperty(SCALE_VAR, text);
};

/**
 * Shrinks one block that is cut at full size, in the browser it is drawn in:
 * `{ scale, atFloor, cut }`, `cut` being what is still cut at the size it ended at
 * (each probe's answer is kept, so the end is not read a second time: a value of
 * thousands of words costs a read for every one). `atFloor` is true when it went all
 * the way down and is still cut, which the notice says in words.
 */
function fitBlock(block, clip, smallestPx, cutAtFull) {
	const lowest = lowestScale(smallestPx);
	const seen = new Map();
	const fitsAt = (scale) => {
		writeScale(block, scale);
		const cut = cutOf(clip, readPieces(block));
		seen.set(scale, cut);
		return !cut;
	};
	// A block at the floor already has no probes to make, and is left at full size.
	const { scale, fits } = scaleToFit(fitsAt, lowest, STEP_PT / (smallestPx * 0.75), { overAtFull: true });
	writeScale(block, scale);
	return { scale, atFloor: !fits && lowest < 1, cut: seen.has(scale) ? seen.get(scale) : cutAtFull };
}

/** What is measured: a block, and a repeating group's cell, which is a block's box for one value (KPR-1863). */
const FITTED = '.page-block[data-fit-name], .page-cell[data-fit-name]';

const blockIdOf = node => node.dataset.blockId || node.closest('.page-block')?.dataset.blockId;

/**
 * Every block under `root` that has something to fit (`data-fit-name`, written by
 * PageLayoutRender on a form) and does not: `{ key, id, name, page, text }`. `key`
 * is unique across the sheets of a query layout, where one block is drawn per record.
 * Null when the sheets have no size to measure against.
 *
 * A repeating group's cell is told as a block is, with `group: true`, and two cells
 * of one key are one row (the designer's lines all hold one sample). Entries that had
 * no line to go on come first: they are the most a record can lose.
 *
 * A block that is also `data-autoscale` is shrunk first, and what is told is what is
 * still cut at the size it ended at: all of them start from full size, in one write
 * pass and one read pass, and only the blocks that are cut are searched, so a sheet
 * of a hundred blocks costs a hundred reads and a few probes for each that overflows.
 */
export function measureBlocks(root) {
	const sheets = [...root.querySelectorAll('.page-sheet')];
	// Nothing has a size (a panel collapsed or not yet shown): every rectangle is zero
	// and everything would "fit". That is no answer, and the caller keeps its last.
	if (sheets.length && sheets.every(sheet => sheet.getBoundingClientRect().width === 0)) return null;
	const blocks = [...root.querySelectorAll(FITTED)];
	const scalable = new Set(blocks.filter(block => block.hasAttribute('data-autoscale')));
	// Every auto scale block back to its set size first: a value that grew, a record
	// that changed, a box that was resized all start from the size the person chose.
	for (const block of scalable) writeScale(block, 1);
	const out = [];
	const told = new Set();
	for (const block of blocks) {
		const clip = clipOf(block);
		const pieces = readPieces(block);
		let cut = cutOf(clip, pieces);
		let atFloor = false;
		if (cut && scalable.has(block)) {
			({ atFloor, cut } = fitBlock(block, clip, smallestFont(pieces), cut));
		}
		if (!cut) continue;
		const sheet = block.closest('.page-sheet');
		const id = blockIdOf(block);
		const { fitCell } = block.dataset;
		const key = `${sheets.indexOf(sheet)}:${id}${fitCell ? `:${fitCell}` : ''}`;
		if (told.has(key)) continue;
		told.add(key);
		out.push({
			key,
			id,
			name: block.dataset.fitName,
			page: Number(sheet?.dataset.formPage) || 1,
			text: `${describeCut(cut)}${atFloor ? `, even at ${FLOOR_PT} pt` : ''}`,
			...(fitCell ? { group: true } : {}),
		});
	}
	const notShown = [...root.querySelectorAll('.page-block[data-not-shown]')].map((block) => {
		const sheet = block.closest('.page-sheet');
		return {
			key: `${sheets.indexOf(sheet)}:${block.dataset.blockId}:not-shown`,
			id: block.dataset.blockId,
			name: block.dataset.groupName,
			page: Number(sheet?.dataset.formPage) || 1,
			text: block.dataset.notShown,
			group: true,
		};
	});
	return [...notShown, ...out];
}

/** The same answer twice: nothing for a component to redraw. */
export const sameCuts = (a, b) => a.length === b.length && a.every((cut, index) => cut.key === b[index].key && cut.text === b[index].text && cut.name === b[index].name);
